import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import Layout, { PageHeader } from "@/components/Layout";
import { DivertBadge } from "@/components/DivertBadge";
import DispatchMap from "@/components/DispatchMap";
import { DiscBadge, PriorityBadge, StatusChip, DISC, MultiAgencyBadge } from "@/components/badges";
import { toast } from "sonner";
import { Send, MessageSquare, Search, Clock, Volume2, VolumeX, Zap, Gauge, ClipboardList, Reply, X, Check, CheckCheck } from "lucide-react";
import { FrequentLocations } from "@/components/FrequentLocations";
import SafetyTimer from "@/components/SafetyTimer";
import WelfareBanner from "@/components/WelfareBanner";
import ShiftBrief from "@/components/ShiftBrief";
import { E911Queue } from "@/components/E911Queue";
import { BoardLocateButton } from "@/components/BoardLocateButton";
import { MobileIncidentCards } from "@/components/MobileIncidentCards";
import { useAuth } from "@/context/AuthContext";
import { blip, hazardTone, emergencyAlarm, messageBeeps, speak } from "@/lib/audio";

const UNIT_STATUS_COLOR = {
  available: "#22C55E", dispatched: "#3B82F6", enroute: "#3B82F6", onscene: "#10B981",
  acknowledged: "#0EA5E9", delayed_onscene: "#EA580C", transport: "#8B5CF6",
  busy: "#F59E0B", outofservice: "#6B7280", directed_patrol: "#0EA5E9",
};

// Jurisdiction-wide response-coverage model (metro Philadelphia bbox). Used for the
// supervisor gap alert + readiness chip; mirrors the map heat overlay (haversine @ 30 mph).
const JURISDICTION = { s: 39.86, n: 40.09, w: -75.33, e: -74.96 };
const COVERAGE_THRESHOLD_MIN = 8;
const COVERAGE_TARGET_PCT = 85;
function _hvKm(lat1, lng1, lat2, lng2) {
  const R = 6371, toR = Math.PI / 180;
  const dLat = (lat2 - lat1) * toR, dLng = (lng2 - lng1) * toR;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * toR) * Math.cos(lat2 * toR) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
function _availWithGps(units) {
  return units.filter((u) => u.status === "available" && !u.current_incident &&
    Number.isFinite(u.lat) && Number.isFinite(u.lng) && !(u.lat === 0 && u.lng === 0));
}
function coveragePct(units, threshold = COVERAGE_THRESHOLD_MIN, hotspots = []) {
  const avail = _availWithGps(units);
  const rows = 12, cols = 16;
  const dLat = (JURISDICTION.n - JURISDICTION.s) / rows, dLng = (JURISDICTION.e - JURISDICTION.w) / cols;
  let covered = 0, total = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const s = JURISDICTION.s + r * dLat, w = JURISDICTION.w + c * dLng;
      const clat = s + dLat / 2, clng = w + dLng / 2;
      // Demand weighting: cells with historical hot-spots (90-day frequent-call
      // clusters) count up to 5x, so coverage % reflects where calls actually happen.
      let demand = 0;
      hotspots.forEach((hs) => {
        if (hs.lat >= s && hs.lat < s + dLat && hs.lng >= w && hs.lng < w + dLng) demand += hs.weight;
      });
      const wgt = 1 + 4 * Math.min(1, demand);
      total += wgt;
      let bestKm = Infinity;
      avail.forEach((u) => { const km = _hvKm(clat, clng, u.lat, u.lng); if (km < bestKm) bestKm = km; });
      const min = avail.length ? (bestKm / 48.28) * 60 : 9999;
      if (min <= threshold) covered += wgt;
    }
  }
  return total ? Math.round((covered / total) * 100) : 0;
}
function nearestAvailableUnit(units, inc) {
  const il = inc.location;
  if (!il || !Number.isFinite(il.lat) || !Number.isFinite(il.lng)) return null;
  let best = null, bestD = Infinity;
  _availWithGps(units).forEach((u) => {
    const dx = u.lat - il.lat, dy = u.lng - il.lng, d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = u; }
  });
  return best;
}

const ref = (i) => i?.public_ref ? `CAD# ${i.public_ref}` : i?.cc_number;
const ackAlert = async (i, kind) => {
  try { await client.post(`/incidents/${i.id}/ack-alert`, { kind }); toast.success(`Acknowledged — ${ref(i)} (logged)`); } catch (e) { toast.error(e.response?.data?.detail || "Ack failed"); }
};

export default function Dispatch() {
  const [incidents, setIncidents] = useState([]);
  const [units, setUnits] = useState([]);
  const [filter, setFilter] = useState("");
  const [unitQuery, setUnitQuery] = useState("");
  const [sel, setSel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [msgFilter, setMsgFilter] = useState("all");
  const [replyTo, setReplyTo] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [preemptInc, setPreemptInc] = useState(null);
  const [trafficInc, setTrafficInc] = useState(null);
  const [pUnit, setPUnit] = useState("");
  const [pNote, setPNote] = useState("");
  const [pBusy, setPBusy] = useState(false);
  const [assignInc, setAssignInc] = useState(null);
  const [aQuery, setAQuery] = useState("");
  const [aBusy, setABusy] = useState(null);
  // Holding-call alarm: escalating tone when a pending call sits unassigned too long.
  const [holdMin, setHoldMin] = useState(() => Number(localStorage.getItem("dispatch_hold_alarm") ?? 3));
  const setHoldAlarm = (m) => { localStorage.setItem("dispatch_hold_alarm", String(m)); setHoldMin(m); };
  const holdLastTone = useRef({});
  const [nowTs, setNowTs] = useState(Date.now());
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  const prevStatuses = useRef({});
  const initedRef = useRef(false);
  const knownInc = useRef(new Set());
  const incInited = useRef(false);
  const knownBackup = useRef(new Set());
  const backupInited = useRef(false);
  const knownOnview = useRef(new Set());
  const onviewInited = useRef(false);
  const knownReassign = useRef(new Set());
  const reassignInited = useRef(false);
  const [covPct, setCovPct] = useState(null);
  const [briefOpen, setBriefOpen] = useState(false);
  const [hotspots, setHotspots] = useState([]);
  const hotspotsRef = useRef([]);
  const coverageInited = useRef(false);
  const gapOkRef = useRef(true);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { const t = setInterval(() => setNowTs(Date.now()), 1000); return () => clearInterval(t); }, []);

  // Historical hot-spots (90-day frequent-call clusters) weight the coverage model;
  // refreshed every 10 minutes — demand patterns move slowly.
  useEffect(() => {
    const fetchHs = () => client.get("/coverage/hotspots")
      .then((r) => { hotspotsRef.current = r.data.hotspots || []; setHotspots(hotspotsRef.current); })
      .catch(() => {});
    fetchHs();
    const t = setInterval(fetchHs, 600000);
    return () => clearInterval(t);
  }, []);

  const playTone = (freq) => {
    if (mutedRef.current) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = freq; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.start(); o.stop(ctx.currentTime + 0.37);
    } catch { /* audio unavailable */ }
  };

  const elapsed = (since) => {
    if (!since) return null;
    const s = Math.max(0, Math.floor((nowTs - new Date(since).getTime()) / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  const load = useCallback(async () => {
    const [inc, un, msg] = await Promise.all([
      client.get("/incidents", { params: filter ? { discipline: filter } : {} }),
      client.get("/units", { params: filter ? { discipline: filter } : {} }),
      client.get("/messages", { params: { everything: true } }),
    ]);
    // Audible cue when a unit newly arrives on scene or goes delayed (skip first load).
    un.data.forEach((u) => {
      const prev = prevStatuses.current[u.unit_id];
      if (initedRef.current && prev && prev !== u.status) {
        if (u.status === "onscene") playTone(880);
        else if (u.status === "delayed_onscene") playTone(392);
      }
      prevStatuses.current[u.unit_id] = u.status;
    });
    initedRef.current = true;
    // Distinct urgent two-tone when a NEW P1 incident drops (skip first load).
    inc.data.forEach((i) => {
      if (i.priority === 1 && incInited.current && !knownInc.current.has(i.cc_number)) {
        playTone(1046); setTimeout(() => playTone(1319), 180);
        toast.error(`⚠ NEW PRIORITY-1 — ${i.call_type_label} @ ${i.location?.address}`, { duration: 9000 });
      }
      knownInc.current.add(i.cc_number);
    });
    incInited.current = true;
    // Officer-initiated activity (traffic stop / on-view from the MDT): distinct tone + toast.
    inc.data.forEach((i) => {
      if (i.officer_initiated && onviewInited.current && !knownOnview.current.has(i.cc_number)) {
        playTone(587); setTimeout(() => playTone(880), 150); setTimeout(() => playTone(587), 300);
        toast.warning(`🚔 UNIT OUT ON-VIEW — ${i.officer_initiated.unit} ${i.officer_initiated.label.toUpperCase()} @ ${i.location?.address}${i.officer_initiated.plate ? ` · ${i.officer_initiated.plate}` : ""}${i.officer_initiated.high_risk ? " · HIGH-RISK" : ""}`, { duration: 12000 });
      }
      if (i.officer_initiated) knownOnview.current.add(i.cc_number);
    });
    // Plate hit / lapsed stop timer on officer-initiated stops: alarm + toast.
    inc.data.forEach((i) => {
      const k1 = `plate-${i.cc_number}`, k2 = `timer-${i.cc_number}`, k3 = `watch-${i.cc_number}`;
      if (i.plate_watch_hit && onviewInited.current && !knownOnview.current.has(k3)) {
        playTone(1046); setTimeout(() => playTone(1318), 150); setTimeout(() => playTone(1046), 300); setTimeout(() => playTone(1318), 450);
        toast.error(`🚩 WATCHLIST PLATE SIGHTED — ${i.plate_watch_hit.plate}: ${i.plate_watch_hit.watches.map((w) => w.reason).join(" · ")} · ${i.plate_watch_hit.how} @ ${i.location?.address}`, { duration: 20000 });
      }
      if (i.plate_watch_hit) knownOnview.current.add(k3);
      if (i.plate_run?.count > 0 && onviewInited.current && !knownOnview.current.has(k1)) {
        playTone(1046); setTimeout(() => playTone(784), 160); setTimeout(() => playTone(1046), 320);
        toast.error(`🚨 PLATE HIT — ${i.officer_initiated?.unit} · ${i.plate_run.plate}: ${i.plate_run.hits[0]?.source} ${i.plate_run.hits[0]?.detail}`, { duration: 15000 });
      }
      if (i.plate_run?.count > 0) knownOnview.current.add(k1);
      if (i.stop_timer_alert && onviewInited.current && !knownOnview.current.has(k2)) {
        playTone(660); setTimeout(() => playTone(494), 200); setTimeout(() => playTone(660), 400);
        toast.warning(`⏱ STOP TIMER LAPSED — ${i.officer_initiated?.unit} @ ${i.location?.address}${i.stop_timer_alert.nearest ? ` · ${i.stop_timer_alert.nearest} sent for cover check` : ""}`, { duration: 15000 });
      }
      if (i.stop_timer_alert) knownOnview.current.add(k2);
      const k4 = `${i.cc_number}:assist:${i.outside_assist?.lapsed_ts || ""}`;
      if (i.outside_assist?.lapsed && !i.outside_assist.arrived_ts && onviewInited.current && !knownOnview.current.has(k4)) {
        playTone(660); setTimeout(() => playTone(494), 200); setTimeout(() => playTone(660), 400);
        toast.error(`⏱ NO ${i.outside_assist.agency.toUpperCase()} ARRIVAL — ${i.outside_assist.lapse_min || 20} min since ${i.outside_assist.unit} requested assist @ ${i.location?.address}${i.outside_assist.phone ? ` · re-contact ${i.outside_assist.phone}` : ""}`, { duration: 20000 });
      }
      if (i.outside_assist?.lapsed) knownOnview.current.add(k4);
    });
    onviewInited.current = true;
    // Instant alarm when a unit raises BACKUP REQUESTED (skip first load).
    inc.data.forEach((i) => {
      if (i.backup_requested && backupInited.current && !knownBackup.current.has(i.cc_number)) {
        playTone(1046); setTimeout(() => playTone(784), 160); setTimeout(() => playTone(1046), 320);
        toast.error(`🚨 BACKUP REQUESTED — ${i.call_type_label} @ ${i.location?.address} (by ${i.backup_requested.by})`, { duration: 12000 });
      }
      if (i.backup_requested) knownBackup.current.add(i.cc_number); else knownBackup.current.delete(i.cc_number);
    });
    backupInited.current = true;
    // Attention chime + toast the instant a call flips to NEEDS RE-ASSIGNMENT (skip first load).
    inc.data.forEach((i) => {
      if (i.needs_reassignment && reassignInited.current && !knownReassign.current.has(i.cc_number)) {
        playTone(660); setTimeout(() => playTone(494), 200);
        toast.warning(`⚠ NEEDS RE-ASSIGNMENT — ${i.call_type_label} @ ${i.location?.address} has no unit`, { duration: 12000 });
      }
      if (i.needs_reassignment) knownReassign.current.add(i.cc_number); else knownReassign.current.delete(i.cc_number);
    });
    reassignInited.current = true;
    // Supervisor coverage-gap alert: edge-triggered when jurisdiction coverage drops
    // below target (a gap opens), so command can reposition patrols proactively.
    const pct = coveragePct(un.data, COVERAGE_THRESHOLD_MIN, hotspotsRef.current);
    setCovPct(pct);
    const ok = pct >= COVERAGE_TARGET_PCT;
    const isSup = ["supervisor", "watch_commander", "admin"].includes(user?.role);
    if (coverageInited.current && isSup && !ok && gapOkRef.current) {
      playTone(587); setTimeout(() => playTone(440), 220);
      toast.warning(`⚠ COVERAGE GAP — only ${pct}% of demand-weighted jurisdiction is within ${COVERAGE_THRESHOLD_MIN} min of an available unit. Reposition patrols toward hot-spots.`, { duration: 12000 });
    }
    gapOkRef.current = ok;
    coverageInited.current = true;
    setIncidents(inc.data); setUnits(un.data); setMessages(msg.data);
  }, [filter, user?.role]);
  useEffect(() => { load(); const t = setInterval(load, 2000); return () => clearInterval(t); }, [load]);
  useRealtime(["incidents", "units", "messages"], () => load());

  const sendMsg = async () => {
    if (!msgText.trim()) return;
    if (replyTo) {
      await client.post("/messages", { text: msgText, scope: "unit", channel: "unit", to_unit: replyTo, priority: urgent });
      toast.success(`${urgent ? "URGENT direct" : "Direct"} message sent to ${replyTo}`);
    } else {
      await client.post("/messages", { text: msgText, scope: "broadcast", priority: urgent });
      if (urgent) toast.success("URGENT broadcast sent — all MDTs will alarm");
    }
    setMsgText(""); setUrgent(false); load();
  };
  const msgIsUnread = (m) => m.from_user_id !== user?.id && !(m.reads || []).includes(user?.name);
  const markAllSeen = async () => {
    const ids = messages.filter(msgIsUnread).map((m) => m.id);
    if (!ids.length) return;
    await client.post("/messages/read", { ids });
    load();
  };

  const active = incidents.filter((i) => i.status !== "closed")
    .sort((a, b) => (b.needs_reassignment ? 1 : 0) - (a.needs_reassignment ? 1 : 0)
      || (a.priority || 9) - (b.priority || 9)
      || new Date(b.created_at) - new Date(a.created_at));
  const boardUnits = units.filter((u) => u.status !== "outofservice");
  const onCallUnits = boardUnits.filter((u) => u.current_incident);
  const shownMsgs = msgFilter === "group" ? messages.filter((m) => !m.to_unit && m.scope !== "unit")
    : msgFilter === "direct" ? messages.filter((m) => m.to_unit || m.scope === "unit")
    : messages;
  const uq = unitQuery.trim().toLowerCase();
  const shownUnits = uq
    ? units.filter((u) => `${u.unit_id} ${u.personnel || ""} ${u.county || ""}`.toLowerCase().includes(uq))
    : units;
  const availUnits = units.filter((u) => u.status === "available");

  const dispatch = async (unitId) => {
    if (!sel) { toast.error("Select an incident first"); return; }
    await client.post(`/incidents/${sel.id}/dispatch`, { unit_ids: [unitId] });
    toast.success(`${unitId} dispatched to ${ref(sel)}`);
    load();
  };

  const dispatchNearest = async (inc, e) => {
    e.stopPropagation();
    const u = nearestAvailableUnit(units, inc);
    if (!u) { toast.error("No available unit with GPS to dispatch"); return; }
    try {
      await client.post(`/incidents/${inc.id}/dispatch`, { unit_ids: [u.unit_id] });
      const km = _hvKm(inc.location.lat, inc.location.lng, u.lat, u.lng);
      const min = Math.max(1, Math.round((km / 48.28) * 60));
      toast.success(`${u.unit_id} dispatched (closest, ~${min} min) to ${ref(inc)}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Dispatch failed"); }
  };

  const openPreemptBoard = (inc, e) => { e.stopPropagation(); setPreemptInc(inc); setPUnit(""); setPNote(""); };

  // Assign-to-holding-call picker: available units assign cleanly (existing /dispatch
  // endpoint — no pre-emption); busy units are clearly labeled PRE-EMPT.
  const openAssignPicker = (inc, e) => { e.stopPropagation(); setAssignInc(inc); setAQuery(""); };
  const assignPick = async (u) => {
    setABusy(u.unit_id);
    try {
      if (u.status === "available") {
        await client.post(`/incidents/${assignInc.id}/dispatch`, { unit_ids: [u.unit_id] });
        toast.success(`${u.unit_id} assigned to ${ref(assignInc)}`);
      } else {
        const { data } = await client.post(`/incidents/${assignInc.id}/preempt`,
          { unit_id: u.unit_id, note: "Pre-empted via holding-call assign picker" });
        toast.success(data.pulled_from ? `${u.unit_id} PRE-EMPTED from ${data.pulled_from} → ${ref(assignInc)}` : `${u.unit_id} assigned to ${ref(assignInc)}`);
      }
      setAssignInc(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Assignment failed"); }
    finally { setABusy(null); }
  };

  const submitPreemptBoard = async () => {
    if (!pUnit) { toast.error("Select a unit"); return; }
    setPBusy(true);
    try {
      const { data } = await client.post(`/incidents/${preemptInc.id}/preempt`, { unit_id: pUnit, note: pNote });
      toast.success(data.pulled_from ? `${pUnit} pre-empted from ${data.pulled_from}` : `${pUnit} assigned to ${ref(preemptInc)}`);
      setPreemptInc(null); setPUnit(""); setPNote(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Pre-empt failed"); }
    finally { setPBusy(false); }
  };

  const setUnitStatus = async (unitId, status, label) => {
    try {
      await client.patch(`/units/${unitId}/status`, { status });
      toast.success(`${unitId} — ${label}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); }
  };

  const holdingAge = (i) => (i.status === "pending" && !(i.assigned_units || []).length && i.created_at)
    ? (Date.now() - new Date(i.created_at).getTime()) / 60000 : 0;

  // Escalating holding-call alarm sweep (respects the console mute toggle).
  useEffect(() => {
    if (!holdMin) return;
    const t = setInterval(() => {
      if (mutedRef.current) return;
      const now = Date.now();
      incidents.forEach((i) => {
        const age = holdingAge(i);
        if (age < holdMin) { delete holdLastTone.current[i.cc_number]; return; }
        const level = age >= holdMin * 3 ? 3 : age >= holdMin * 2 ? 2 : 1;
        const gapMs = level === 3 ? 20000 : level === 2 ? 30000 : 60000; // escalates: more frequent + harsher
        const last = holdLastTone.current[i.cc_number] || 0;
        if (now - last < gapMs) return;
        holdLastTone.current[i.cc_number] = now;
        if (level === 3) emergencyAlarm(); else if (level === 2) hazardTone(); else blip();
        toast.warning(`⏳ HOLDING ${Math.floor(age)} min — ${ref(i)} ${i.call_type_label} has no unit assigned`, { duration: 6000 });
      });
    }, 10000);
    return () => clearInterval(t);
  }, [incidents, holdMin]);

  // Caller Chat Alerts — tone + toast when a call's TEXT FROM 911 unread marker advances.
  const callerTextRef = useRef(null);
  useEffect(() => {
    const cur = {};
    incidents.forEach((i) => { if (i.caller_text_unread?.count > 0) cur[i.cc_number] = i.caller_text_unread.ts; });
    if (callerTextRef.current !== null) {
      incidents.forEach((i) => {
        const ts = cur[i.cc_number];
        if (ts && ts !== callerTextRef.current[i.cc_number]) {
          if (!mutedRef.current) { messageBeeps(); setTimeout(() => speak(`Text from caller on ${i.public_ref ? `CAD ${i.public_ref}` : "a call"}.`), 700); }
          toast(`💬 TEXT FROM CALLER — ${ref(i)}: ${i.caller_text_unread.preview}`, { duration: 10000, action: { label: "Open", onClick: () => nav(`/incident/${i.id}`) } });
        }
      });
    }
    callerTextRef.current = cur;
  }, [incidents]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <PageHeader title="Dispatch Board" sub="Assign available units to active incidents"
        right={
          <div className="flex items-center gap-2">
            <DivertBadge />
            <button data-testid="dispatch-shift-brief-btn" onClick={() => setBriefOpen(true)}
              title="Shift Handoff Briefing — open calls, hazards, BOLOs"
              className="flex items-center gap-1.5 px-2.5 py-1.5 border text-xs font-head bg-[#E0B23C]/10 border-[#E0B23C]/50 text-[#E0B23C] hover:bg-[#E0B23C]/20 transition-colors">
              <ClipboardList size={14} /> SHIFT BRIEF
            </button>
            {covPct != null && (
              <div data-testid="coverage-readiness" title={`Coverage: ${covPct}% of the jurisdiction (weighted toward 90-day call hot-spots) is within ${COVERAGE_THRESHOLD_MIN} min of an available unit`}
                className="flex items-center gap-1.5 px-2.5 py-1.5 border text-xs font-head"
                style={{ background: "#141518", borderColor: covPct >= COVERAGE_TARGET_PCT ? "#22C55E55" : "#DC262655", color: covPct >= COVERAGE_TARGET_PCT ? "#22C55E" : "#DC2626" }}>
                <Gauge size={14} /> {covPct}% ≤{COVERAGE_THRESHOLD_MIN}m
              </div>
            )}
            <div className="flex items-center gap-1 px-1.5 py-1 border border-[#2C2D35] bg-[#141518]" title="Holding-call alarm — escalating tone when a pending call sits unassigned past this many minutes" data-testid="dispatch-hold-alarm">
              <Clock size={12} className="text-[#F59E0B]" />
              <span className="font-mono text-[9px] text-[#9CA3AF] uppercase">Hold</span>
              {[0, 2, 3, 5, 10].map((m) => (
                <button key={m} data-testid={`dispatch-hold-alarm-${m}`} onClick={() => setHoldAlarm(m)}
                  className={`px-1.5 py-0.5 text-[10px] font-head font-bold transition-colors ${holdMin === m ? "bg-[#F59E0B] text-black" : "text-[#6B7280] hover:text-white"}`}>
                  {m === 0 ? "OFF" : `${m}m`}
                </button>
              ))}
            </div>
            <button data-testid="dispatch-mute-toggle"
              onClick={() => { const m = !muted; setMuted(m); mutedRef.current = m; }}
              title={muted ? "Status tones muted" : "Status tones on"}
              className={`px-2.5 py-1.5 border transition-colors ${muted ? "bg-[#141518] border-[#2C2D35] text-[#6B7280]" : "bg-[#0EA5E9]/15 border-[#0EA5E9] text-[#0EA5E9]"}`}>
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            <div className="flex gap-1">
              {["", "police", "fire", "ems"].map((f) => (
                <button key={f || "all"} data-testid={`filter-${f || "all"}`} onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 text-xs font-head border transition-colors ${filter === f ? "bg-[#3B82F6] border-[#3B82F6] text-white" : "bg-[#141518] border-[#2C2D35] text-[#9CA3AF] hover:text-white"}`}>
                  {f ? f.toUpperCase() : "ALL"}
                </button>
              ))}
            </div>
          </div>
        } />
      <div className="hidden md:block"><WelfareBanner /></div>
      <details className="md:hidden border-b border-[#2C2D35] bg-[#0d0e11]" data-testid="mobile-welfare-collapse">
        <summary className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-[#F59E0B] cursor-pointer select-none">Welfare timer &amp; escalations ▾</summary>
        <WelfareBanner />
      </details>
      <E911Queue incidents={active} onPick={(r) => nav(`/new-call?e911_lat=${r.lat}&e911_lng=${r.lng}${r.phone ? `&e911_phone=${encodeURIComponent(r.phone)}` : ""}`)} />
      <div className="flex-1 overflow-y-auto lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_360px]">
        {/* left: map + incidents */}
        <div className="lg:overflow-y-auto border-r border-[#2C2D35]">
          <DispatchMap units={units} incidents={active} height="300px" onSelectIncident={(i) => setSel(i)} showRoutes selectedIncidentId={sel?.id} enableCoverage hotspots={hotspots} />
          <div className="px-3 py-2 border-y border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">
            ACTIVE INCIDENTS ({active.length}) {sel && <span className="text-[#3B82F6]">· SELECTED {ref(sel)}</span>}
          </div>
          <MobileIncidentCards incidents={active} selected={sel} onSelect={setSel} onAssign={openAssignPicker} onPreempt={openPreemptBoard} onNearest={dispatchNearest} onOpen={(i) => nav(`/incident/${i.id}`)} refLabel={ref} units={units} onUnitStatus={setUnitStatus}
            holdFlag={(i) => holdMin > 0 && holdingAge(i) >= holdMin ? <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold whitespace-nowrap ${holdingAge(i) >= holdMin * 3 ? "bg-[#DC2626] text-white animate-pulse" : holdingAge(i) >= holdMin * 2 ? "bg-[#EA580C] text-white animate-pulse" : "bg-[#F59E0B]/25 text-[#F59E0B] border border-[#F59E0B]/60"}`}>⏳ HOLDING {Math.floor(holdingAge(i))}m</span> : null} />
          <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-left min-w-[1040px]">
            <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase bg-[#0d0e11] sticky top-0">
              <tr><th className="px-3 py-2">Pri</th><th className="px-2">Case #</th><th className="px-2">CAD #</th><th className="px-2">Received</th><th className="px-2">Type</th><th className="px-2">Location</th><th className="px-2">Units</th><th className="px-2">Status</th><th className="px-2">Safety Timer</th><th className="px-2">Action</th></tr>
            </thead>
            <tbody>
              {active.map((i) => (
                <tr key={i.id} data-testid={`dispatch-row-${i.cc_number}`} onClick={() => setSel(i)}
                  className={`border-b border-[#2C2D35] cursor-pointer hover:bg-[#141518] ${sel?.id === i.id ? "bg-[#141518]" : ""} ${i.caller_text_unread?.count > 0 ? "caller-text-flash" : ""}`}>
                  <td className="px-3 py-2"><PriorityBadge priority={i.priority} /></td>
                  <td className="px-2 font-mono text-[11px]">{i.cc_number}</td>
                  <td className="px-2 font-mono text-[11px] text-[#22D3EE]" data-testid={`dispatch-cadnum-${i.cc_number}`}>{i.public_ref || "—"}</td>
                  <td className="px-2 font-mono text-[10px] text-[#9CA3AF] whitespace-nowrap" data-testid={`dispatch-received-${i.cc_number}`}>{i.created_at ? new Date(i.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}</td>
                  <td className="px-2 text-sm"><div className="flex items-center gap-1.5 flex-wrap"><DiscBadge discipline={i.discipline} />{i.multi_agency && <MultiAgencyBadge />}{i.call_type_label}{i.caller_text_unread?.count > 0 && <button data-testid={`dispatch-caller-text-${i.cc_number}`} onClick={(e) => { e.stopPropagation(); nav(`/incident/${i.id}`); }} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#2563EB] text-white animate-pulse" title={`${i.caller_text_unread.preview}\n\nClick to open the caller chat`}>💬 TEXT FROM CALLER · {i.caller_text_unread.count}</button>}{i.late_entry && <span data-testid={`dispatch-late-entry-${i.cc_number}`} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#F59E0B]/20 text-[#FCD34D] border border-[#F59E0B]/60" title={`LATE ENTRY by ${i.late_entry.entered_by} at ${(i.late_entry.entered_at || "").slice(11, 16)}Z — ${i.late_entry.reason}${i.late_entry.recording_ref ? `\nRecording: ${i.late_entry.recording_ref}` : ""}`}>⏮ LATE ENTRY</span>}{i.outside_assist && !i.outside_assist.arrived_ts && (i.outside_assist.lapsed && !i.outside_assist.acknowledged
  ? <button data-testid={`dispatch-assist-lapse-${i.cc_number}`} onClick={(e) => { e.stopPropagation(); client.post(`/incidents/${i.id}/assist-ack`).then(() => toast.success("Lapse acknowledged — re-contact agency")).catch(() => toast.error("Ack failed")); }} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#DC2626] text-white animate-pulse" title={`${i.outside_assist.agency} has not arrived in ${i.outside_assist.lapse_min || 20} min${i.outside_assist.phone ? ` — re-contact ${i.outside_assist.phone}` : ""}\n\nClick to ACKNOWLEDGE`}>⏱ NO {i.outside_assist.agency.split(" — ")[0].toUpperCase()} ARRIVAL · ACK</button>
  : <span data-testid={`dispatch-assist-pending-${i.cc_number}`} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#B45309]/30 text-[#FCD34D] border border-[#F59E0B]/60" title={`${i.outside_assist.agency} assist requested by ${i.outside_assist.unit} at ${(i.outside_assist.requested_ts || "").slice(11, 16)}Z — 20-min arrival timer running`}>🚔 {i.outside_assist.agency.split(" — ")[0].toUpperCase()} ASSIST · {i.outside_assist.unit}</span>)}{i.outside_assist?.arrived_ts && <span data-testid={`dispatch-assist-arrived-${i.cc_number}`} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#166534]/40 text-[#BBF7D0] border border-[#22C55E]/50">✓ {i.outside_assist.agency.split(" — ")[0].toUpperCase()} ARRIVED{i.outside_assist.response_min != null ? ` · ${i.outside_assist.response_min}m` : ""}</span>}{i.psp_assist && !i.outside_assist && <span data-testid={`dispatch-psp-assist-${i.cc_number}`} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#B45309]/30 text-[#FCD34D] border border-[#F59E0B]/60" title={`PSP assist requested by ${i.psp_assist.unit} at ${(i.psp_assist.ts || "").slice(11, 16)}Z — ${i.psp_assist.station || ""} ${i.psp_assist.phone || ""}`}>🚔 PSP ASSIST · {i.psp_assist.unit}</span>}{i.additional_callers?.length > 0 && <span data-testid={`dispatch-callers-badge-${i.cc_number}`} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#EF4444]/20 text-[#FCA5A5] border border-[#EF4444]/50" title={`${i.additional_callers.length + 1} people reporting this incident:\n` + i.additional_callers.map((c) => `${c.name || "Unknown"} ${c.phone || ""}`).join("\n")}>☎ +{i.additional_callers.length} CALLER{i.additional_callers.length > 1 ? "S" : ""}</span>}{i.repeat_address && <span data-testid={`dispatch-repeat-flag-${i.cc_number}`} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/50" title={`${i.repeat_address.count} calls at this address`}>⟳ REPEAT {i.repeat_address.count}x</span>}{i.officer_initiated && <span data-testid={`dispatch-onview-flag-${i.cc_number}`} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#3B82F6]/20 text-[#60A5FA] border border-[#3B82F6]/50" title={`${i.officer_initiated.label} self-initiated by ${i.officer_initiated.unit}${i.officer_initiated.plate ? ` · ${i.officer_initiated.plate}` : ""}`}>🚔 OFFICER-INITIATED · {i.officer_initiated.unit}</span>}{i.plate_watch_hit && !i.plate_watch_hit.acknowledged && <button data-testid={`dispatch-watch-hit-${i.cc_number}`} onClick={(e) => { e.stopPropagation(); ackAlert(i, "plate_watch_hit"); }} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#DC2626] text-white animate-pulse" title={i.plate_watch_hit.watches.map((w) => `${w.reason}${w.note ? `: ${w.note}` : ""} (by ${w.by})`).join("\n") + "\n\nClick to ACKNOWLEDGE"}>🚩 WATCHLIST PLATE · {i.plate_watch_hit.plate} · ACK</button>}{i.plate_run?.count > 0 && !i.plate_run.acknowledged && <button data-testid={`dispatch-plate-hit-${i.cc_number}`} onClick={(e) => { e.stopPropagation(); ackAlert(i, "plate_run"); }} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#DC2626] text-white animate-pulse" title={i.plate_run.hits.map((h) => `${h.source}: ${h.detail}`).join("\n") + "\n\nClick to ACKNOWLEDGE"}>🚨 PLATE HIT · {i.plate_run.plate} · ACK</button>}{i.driver_run?.count > 0 && !i.driver_run.acknowledged && <button data-testid={`dispatch-driver-hit-${i.cc_number}`} onClick={(e) => { e.stopPropagation(); ackAlert(i, "driver_run"); }} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#DC2626] text-white animate-pulse" title={i.driver_run.hits.map((h) => `${h.source}: ${h.detail}`).join("\n") + "\n\nClick to ACKNOWLEDGE"}>🚨 DRIVER HIT · {i.driver_run.name} · ACK</button>}{i.stop_timer_alert && !i.stop_timer_alert.acknowledged && <button data-testid={`dispatch-stop-timer-${i.cc_number}`} onClick={(e) => { e.stopPropagation(); ackAlert(i, "stop_timer_alert"); }} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#F59E0B] text-black animate-pulse" title={(i.stop_timer_alert.nearest ? `Nearest unit ${i.stop_timer_alert.nearest} notified` : "No nearby unit with GPS") + "\n\nClick to ACKNOWLEDGE"}>⏱ STOP TIMER LAPSED · ACK</button>}{i.backup_requested && <span data-testid={`dispatch-backup-flag-${i.cc_number}`} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#DC2626] text-white animate-pulse">⚠ BACKUP</span>}{i.needs_reassignment && <span data-testid={`dispatch-needs-reassign-${i.cc_number}`} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#7C3AED] text-white animate-pulse" title={i.needs_reassignment.reason}>⚠ NEEDS RE-ASSIGN</span>}</div></td>
                  <td className="px-2 font-mono text-[11px] text-[#9CA3AF]"><span className="inline-flex items-center flex-wrap gap-y-1">{i.location?.common_name && <span className="text-[#FCD34D] mr-1" data-testid={`dispatch-common-name-${i.cc_number}`}>{i.location.common_name} ·</span>}{i.location?.address}{i.origin !== "officer" && <BoardLocateButton incident={i} />}</span></td>
                  <td className="px-2 font-mono text-[11px]" data-testid={`dispatch-units-${i.cc_number}`}>
                    {(i.assigned_units || []).length === 0 ? "—" : (
                      <span>{(i.assigned_units || []).join(", ")}
                        <span className="ml-1 text-[#10B981]">({(i.assigned_units || []).length} resp{(i.backup_units || []).length ? `, ${(i.backup_units || []).length} bkup` : ""})</span>
                      </span>
                    )}
                  </td>
                  <td className="px-2">
                    <div className="flex items-center gap-1.5">
                      <StatusChip status={i.status} />
                      {holdMin > 0 && holdingAge(i) >= holdMin && (
                        <span data-testid={`dispatch-holding-${i.cc_number}`}
                          className={`px-1.5 py-0.5 rounded text-[9px] font-bold whitespace-nowrap ${holdingAge(i) >= holdMin * 3 ? "bg-[#DC2626] text-white animate-pulse" : holdingAge(i) >= holdMin * 2 ? "bg-[#EA580C] text-white animate-pulse" : "bg-[#F59E0B]/25 text-[#F59E0B] border border-[#F59E0B]/60"}`}
                          title="No unit assigned — holding past the alarm threshold">
                          ⏳ HOLDING {Math.floor(holdingAge(i))}m
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2"><SafetyTimer incident={i} onChange={load} /></td>
                  <td className="px-2">
                    <div className="flex flex-col gap-1">
                      {i.priority === 1 && (
                        <button data-testid={`dispatch-nearest-${i.cc_number}`} onClick={(e) => dispatchNearest(i, e)}
                          className="flex items-center gap-1 px-2 py-1 text-[10px] font-head border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/10 rounded transition-colors whitespace-nowrap" title="Dispatch the closest available unit">
                          <Zap size={11} /> Dispatch nearest
                        </button>
                      )}
                      <button data-testid={`dispatch-assign-${i.cc_number}`} onClick={(e) => openAssignPicker(i, e)}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-head border border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10 rounded transition-colors whitespace-nowrap" title="Assign an available unit to this call (no pre-emption)">
                        <Check size={11} /> Assign Unit
                      </button>
                      <button data-testid={`dispatch-preempt-${i.cc_number}`} onClick={(e) => openPreemptBoard(i, e)}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-head border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10 rounded transition-colors whitespace-nowrap" title="Pre-empt / add a unit to this call">
                        <Send size={11} /> Pre-empt
                      </button>
                      <button data-testid={`dispatch-traffic-${i.cc_number}`} onClick={(e) => { e.stopPropagation(); setTrafficInc(i); }}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-head border border-[#33CCFF] text-[#33CCFF] hover:bg-[#33CCFF]/10 rounded transition-colors whitespace-nowrap" title="Live Waze traffic around this call — factor congestion into who you send">
                        <Gauge size={11} /> Traffic
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* UNIT STATUS BOARD — active units, status & their active call in one view */}
          <div className="border-t border-[#2C2D35]" data-testid="unit-status-board">
            <div className="px-3 py-2 border-y border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold sticky top-0 z-10">
              UNIT STATUS BOARD · {boardUnits.length} ON DUTY · <span className="text-[#3B82F6]">{onCallUnits.length} ON CALLS</span>
            </div>
            <div className="overflow-x-auto"><table className="w-full text-left min-w-[640px]">
              <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase bg-[#0d0e11]">
                <tr><th className="px-3 py-2">Unit</th><th className="px-2">Status</th><th className="px-2">Time</th><th className="px-2">Active Call</th></tr>
              </thead>
              <tbody>
                {boardUnits.map((u) => {
                  const cInc = incidents.find((i) => i.status !== "closed" && (i.assigned_units || []).includes(u.unit_id));
                  const c = (DISC[u.type] || {}).color;
                  return (
                    <tr key={u.unit_id} data-testid={`board-unit-${u.unit_id}`}
                      onClick={() => cInc && setSel(cInc)}
                      className={`border-b border-[#2C2D35] ${cInc ? "cursor-pointer hover:bg-[#141518]" : ""}`}
                      style={{ borderLeft: `4px solid ${UNIT_STATUS_COLOR[u.status] || "#2C2D35"}` }}>
                      <td className="px-3 py-2 font-mono font-bold text-sm" style={{ color: c }}>{u.unit_id}</td>
                      <td className="px-2"><StatusChip status={u.status} /></td>
                      <td className="px-2 font-mono text-[10px] tabular-nums" style={{ color: UNIT_STATUS_COLOR[u.status] || "#9CA3AF" }}>{u.status_since && u.status !== "available" && u.status !== "outofservice" ? elapsed(u.status_since) : "—"}</td>
                      <td className="px-2 font-mono text-[11px]">{cInc ? <span><span className="text-[#3B82F6]">{ref(cInc)}</span> · {cInc.call_type_label} · <span className="text-[#9CA3AF]">{cInc.location?.address}</span></span> : <span className="text-[#6B7280]">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
        </div>

        {/* right: units */}
        <div className="overflow-y-auto bg-[#0d0e11]">
          <div className="px-3 py-2 border-b border-[#2C2D35] font-head text-xs font-semibold sticky top-0 bg-[#0d0e11] z-10">
            AVAILABLE UNITS ({availUnits.length}) · SHOWING {shownUnits.length}
            <div className="mt-2 flex items-center gap-2 bg-[#141518] border border-[#2C2D35] px-2 py-1.5 focus-within:border-[#3B82F6]">
              <Search size={13} className="text-[#6B7280]" />
              <input data-testid="unit-search" value={unitQuery} onChange={(e) => setUnitQuery(e.target.value)}
                placeholder="Search unit # / name / county…" className="flex-1 bg-transparent outline-none text-xs font-mono text-white placeholder:text-[#6B7280]" />
            </div>
          </div>
          {shownUnits.length === 0 && <div className="px-3 py-3 text-[11px] font-mono text-[#9CA3AF]">No units match “{unitQuery}”.</div>}
          {shownUnits.map((u) => {
            const c = (DISC[u.type] || {}).color;
            return (
              <div key={u.unit_id} data-testid={`unit-card-${u.unit_id}`}
                className="px-3 py-2.5 border-b border-[#2C2D35] flex items-center justify-between"
                style={{ borderLeft: `4px solid ${UNIT_STATUS_COLOR[u.status] || "#2C2D35"}` }}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-sm" style={{ color: c }}>{u.unit_id}</span>
                    <StatusChip status={u.status} />
                    {u.status_since && u.status !== "available" && u.status !== "outofservice" && (
                      <span data-testid={`unit-timer-${u.unit_id}`} className="font-mono text-[10px] tabular-nums flex items-center gap-0.5"
                        style={{ color: UNIT_STATUS_COLOR[u.status] || "#9CA3AF" }}>
                        <Clock size={9} />{elapsed(u.status_since)}
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-[#9CA3AF]">{u.personnel}{u.current_incident ? ` · ${u.current_incident}` : ""}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  {u.status !== "available" && u.status !== "delayed_onscene" && (
                    <button data-testid={`delayed-${u.unit_id}`}
                      onClick={() => setUnitStatus(u.unit_id, "delayed_onscene", "Delayed On Scene")}
                      title="Mark Delayed On Scene"
                      className="px-2 py-1 text-[11px] font-head bg-[#EA580C]/15 border border-[#EA580C] text-[#EA580C] hover:bg-[#EA580C]/25 transition-colors flex items-center gap-1">
                      <Clock size={12} /> Delayed
                    </button>
                  )}
                  <button data-testid={`assign-${u.unit_id}`} disabled={u.status !== "available"}
                    onClick={() => dispatch(u.unit_id)}
                    className="px-2.5 py-1 text-[11px] font-head bg-[#1E1F24] border border-[#2C2D35] hover:border-[#3B82F6] hover:text-white transition-colors disabled:opacity-30">
                    Assign
                  </button>
                </div>
              </div>
            );
          })}
          {sel && (
            <button data-testid="open-selected-incident" onClick={() => nav(`/incident/${sel.id}`)}
              className="m-3 w-[calc(100%-1.5rem)] px-3 py-2 text-xs font-head bg-[#3B82F6] hover:bg-[#2563eb] text-white transition-colors">
              Open {ref(sel)} Detail →
            </button>
          )}

          {/* Frequent call locations (repeat / nuisance addresses) */}
          <div className="border-t border-[#2C2D35] mt-2 p-2">
            <FrequentLocations compact limit={8} />
          </div>

          {/* Real-time messaging (CAD ⇄ MDT) */}
          <div className="border-t border-[#2C2D35] mt-2" data-testid="dispatch-messages">
            <div className="px-3 py-2 border-b border-[#2C2D35] font-head text-xs font-semibold flex items-center gap-2 flex-wrap">
              <MessageSquare size={13} /> FIELD MESSAGING (LIVE)
              {messages.filter(msgIsUnread).length > 0 && (
                <button data-testid="dispatch-msg-unread" onClick={markAllSeen} title="Unread messages — click to mark all seen"
                  className="px-2 py-0.5 rounded-full bg-[#DC2626] text-white font-mono text-[9px] font-bold hover:bg-[#b91c1c] transition-colors">
                  {messages.filter(msgIsUnread).length} NEW
                </button>
              )}
              <div className="ml-auto flex gap-1">
                {[["all", "ALL"], ["group", "GROUP"], ["direct", "DIRECT"]].map(([k, lab]) => (
                  <button key={k} data-testid={`dispatch-msgfilter-${k}`} onClick={() => setMsgFilter(k)}
                    className={`px-2 py-0.5 text-[10px] font-head border transition-colors ${msgFilter === k ? "bg-[#3B82F6] border-[#3B82F6] text-white" : "bg-[#141518] border-[#2C2D35] text-[#9CA3AF] hover:text-white"}`}>{lab}</button>
                ))}
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto p-2 space-y-1.5">
              {shownMsgs.length === 0 && <div className="text-[11px] font-mono text-[#9CA3AF]">No {msgFilter === "all" ? "" : msgFilter + " "}messages.</div>}
              {shownMsgs.map((m) => (
                <div key={m.id} className="border-l-2 pl-2 py-0.5" style={{ borderColor: m.priority ? "#DC2626" : (m.to_unit || m.scope === "unit") ? "#10B981" : "#3B82F6", background: msgIsUnread(m) ? "rgba(220,38,38,0.06)" : undefined }}>
                  <div className="font-mono text-[9px] text-[#9CA3AF] flex items-center gap-1">
                    {msgIsUnread(m) && <span data-testid={`msg-unread-dot-${m.id}`} className="w-1.5 h-1.5 rounded-full bg-[#DC2626] shrink-0" />}
                    <span className={`px-1 rounded text-[8px] font-bold ${(m.to_unit || m.scope === "unit") ? "text-[#10B981] border border-[#10B981]" : "text-[#3B82F6] border border-[#3B82F6]"}`}>{(m.to_unit || m.scope === "unit") ? "DIRECT" : "GROUP"}</span>
                    {new Date(m.ts).toLocaleTimeString("en-GB", { hour12: false })} · {m.from_name}{m.from_unit ? ` (${m.from_unit})` : ""} → {m.to_unit || "ALL"}
                    {m.priority && m.from_user_id === user?.id && (() => {
                      const acks = (m.acks || []).filter((n) => n !== user?.name);
                      return acks.length
                        ? <span data-testid={`msg-acked-${m.id}`} title={`Copied by ${acks.join(", ")}`} className="flex items-center gap-0.5 text-[#4ade80] font-bold"><Check size={11} /> COPIED{acks.length > 1 ? ` ×${acks.length}` : ""}</span>
                        : <span data-testid={`msg-awaiting-${m.id}`} title="Urgent — no unit has tapped COPY yet" className="text-[#F59E0B] font-bold animate-pulse">AWAITING COPY</span>;
                    })()}
                    {m.from_user_id === user?.id && (() => {
                      const seen = (m.reads || []).filter((n) => n !== user?.name);
                      return seen.length
                        ? <span data-testid={`msg-seen-${m.id}`} title={`Seen by ${seen.join(", ")}`} className="flex items-center gap-0.5 text-[#4ade80] font-bold"><CheckCheck size={11} /> SEEN{seen.length > 1 ? ` ×${seen.length}` : ""}</span>
                        : <span data-testid={`msg-sent-${m.id}`} title="Delivered — not yet seen" className="flex items-center gap-0.5 text-[#6B7280]"><Check size={11} /> SENT</span>;
                    })()}
                    {m.from_unit && (
                      <button data-testid={`dispatch-reply-${m.id}`} onClick={() => setReplyTo(m.from_unit)} title={`Reply directly to ${m.from_unit}`}
                        className="ml-auto flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-[#10B981]/50 text-[#10B981] hover:bg-[#10B981]/15 transition-colors text-[8px] font-bold">
                        <Reply size={10} /> REPLY
                      </button>
                    )}
                  </div>
                  <div className="text-[11px]" style={{ color: m.priority ? "#EF4444" : "#F3F4F6" }}>{m.text}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-1.5 p-2 border-t border-[#2C2D35] items-center">
              {replyTo && (
                <span data-testid="dispatch-reply-chip" className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono font-bold bg-[#10B981]/15 border border-[#10B981] text-[#10B981] shrink-0">
                  → {replyTo}
                  <button data-testid="dispatch-reply-cancel" onClick={() => setReplyTo("")} title="Back to broadcast" className="hover:text-white"><X size={11} /></button>
                </span>
              )}
              <input data-testid="dispatch-message-input" value={msgText} onChange={(e) => setMsgText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()}
                placeholder={replyTo ? `${urgent ? "URGENT direct" : "Direct"} message to ${replyTo}…` : urgent ? "URGENT broadcast — all MDTs will alarm…" : "Broadcast to all field units…"} className={`flex-1 bg-[#141518] border px-2 py-1.5 text-xs outline-none font-mono ${urgent ? "border-[#DC2626] focus:border-[#DC2626]" : "border-[#2C2D35] focus:border-[#3B82F6]"}`} />
              <button data-testid="dispatch-msg-urgent-toggle" onClick={() => setUrgent((u) => !u)}
                title={urgent ? "URGENT ON — this message will trigger an audible alarm on the MDT" : "Mark message URGENT (audible MDT alarm)"}
                className={`px-2 flex items-center gap-1 text-[10px] font-head font-bold border transition-colors ${urgent ? "bg-[#DC2626] border-[#DC2626] text-white animate-pulse" : "bg-[#141518] border-[#2C2D35] text-[#9CA3AF] hover:text-[#DC2626] hover:border-[#DC2626]"}`}>
                <Zap size={13} /> URGENT
              </button>
              <button data-testid="dispatch-message-send" onClick={sendMsg} className={`px-3 text-white flex items-center ${urgent ? "bg-[#DC2626]" : "bg-[#3B82F6]"}`}><Send size={14} /></button>
            </div>
          </div>
        </div>
      </div>
      {assignInc && (
        <div data-testid="dispatch-assign-modal" className="fixed inset-0 z-[999] flex items-start justify-center bg-black/70 p-4 overflow-y-auto" onClick={() => setAssignInc(null)}>
          <div className="mt-16 w-full max-w-lg bg-[#0d0e11] border border-[#2C2D35]" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-[#2C2D35] flex items-center gap-2 font-head text-sm text-white">
              <Check size={15} className="text-[#10B981]" /> Assign Unit — {ref(assignInc)}
              <button data-testid="dispatch-assign-close" onClick={() => setAssignInc(null)} className="ml-auto text-[#9CA3AF] hover:text-white">✕</button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-[11px] font-mono text-[#9CA3AF]">
                P{assignInc.priority} · {assignInc.call_type_label} · {assignInc.location?.address}
              </div>
              <div className="flex items-center gap-2 bg-[#141518] border border-[#2C2D35] px-2 py-1.5 focus-within:border-[#10B981]">
                <Search size={13} className="text-[#6B7280]" />
                <input data-testid="dispatch-assign-search" autoFocus value={aQuery} onChange={(e) => setAQuery(e.target.value)}
                  placeholder="Search unit # / name / county…" className="flex-1 bg-transparent outline-none text-xs font-mono text-white placeholder:text-[#6B7280]" />
              </div>
              <div className="max-h-[45vh] overflow-y-auto space-y-1" data-testid="dispatch-assign-list">
                {(() => {
                  const q2 = aQuery.trim().toLowerCase();
                  // GPS decision support: distance from live unit fix to the call (dispatcher decides).
                  const il = assignInc.location;
                  const distMi = (u) => {
                    if (!Number.isFinite(il?.lat) || !Number.isFinite(u.lat) || !Number.isFinite(u.lng)) return null;
                    const dy = (u.lat - il.lat) * 69, dx = (u.lng - il.lng) * 69 * Math.cos(il.lat * Math.PI / 180);
                    return Math.sqrt(dx * dx + dy * dy);
                  };
                  const pool = units
                    .filter((u) => u.status !== "outofservice" && !(assignInc.assigned_units || []).includes(u.unit_id))
                    .filter((u) => !q2 || `${u.unit_id} ${u.personnel || ""} ${u.county || ""}`.toLowerCase().includes(q2))
                    .sort((a, b) => {
                      const av = (a.status === "available" ? 0 : 1) - (b.status === "available" ? 0 : 1);
                      if (av) return av;
                      const da = distMi(a), db2 = distMi(b);
                      if (da !== null && db2 !== null && Math.abs(da - db2) > 0.01) return da - db2;
                      if (da === null && db2 !== null) return 1;
                      if (da !== null && db2 === null) return -1;
                      return a.unit_id.localeCompare(b.unit_id);
                    });
                  if (!pool.length) return <div className="text-[11px] font-mono text-[#9CA3AF] py-2">No units match.</div>;
                  return pool.map((u) => {
                    const avail = u.status === "available";
                    const d = distMi(u);
                    return (
                      <div key={u.unit_id} className="flex items-center gap-2 px-2 py-2 border border-[#2C2D35] bg-[#141518]">
                        <span className="font-mono font-bold text-sm" style={{ color: (DISC[u.type] || {}).color }}>{u.unit_id}</span>
                        <StatusChip status={u.status} />
                        {d !== null && <span data-testid={`dispatch-assign-dist-${u.unit_id}`} className="font-mono text-[9px] font-bold px-1 py-0.5 rounded-sm bg-[#22D3EE]/15 text-[#22D3EE] whitespace-nowrap">{d < 10 ? d.toFixed(1) : Math.round(d)} mi</span>}
                        <span className="font-mono text-[10px] text-[#9CA3AF] truncate">{u.personnel}{u.current_incident ? ` · on ${u.current_incident}` : ""}</span>
                        <button data-testid={`dispatch-assign-pick-${u.unit_id}`} disabled={aBusy === u.unit_id}
                          onClick={() => assignPick(u)}
                          className={`ml-auto px-2.5 py-1 text-[11px] font-head font-bold transition-colors disabled:opacity-40 whitespace-nowrap ${avail
                            ? "bg-[#10B981] hover:bg-[#059669] text-black"
                            : "bg-transparent border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10"}`}
                          title={avail ? "Assign this available unit (no pre-emption)" : `Unit is ${u.status}${u.current_incident ? ` on ${u.current_incident}` : ""} — assigning will PRE-EMPT it`}>
                          {aBusy === u.unit_id ? "…" : avail ? "ASSIGN" : "PRE-EMPT"}
                        </button>
                      </div>
                    );
                  });
                })()}
              </div>
              <div className="text-[10px] font-mono text-[#6B7280]">
                Green ASSIGN = unit is available, no one gets pulled. Amber PRE-EMPT = unit is busy and will be pulled off its current status/call (audited).
              </div>
            </div>
          </div>
        </div>
      )}
      {preemptInc && (
        <div data-testid="dispatch-preempt-modal" className="fixed inset-0 z-[999] flex items-start justify-center bg-black/70 p-4 overflow-y-auto" onClick={() => setPreemptInc(null)}>
          <div className="mt-24 w-full max-w-md bg-[#0d0e11] border border-[#2C2D35]" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-[#2C2D35] flex items-center gap-2 font-head text-sm text-white">
              <Send size={15} className="text-[#F59E0B]" /> Pre-empt / Add Unit — {ref(preemptInc)}
              <button data-testid="dispatch-preempt-close" onClick={() => setPreemptInc(null)} className="ml-auto text-[#9CA3AF] hover:text-white">✕</button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-[11px] font-mono text-[#9CA3AF]">{preemptInc.call_type_label} · {preemptInc.location?.address}. Assign any unit (a busy unit is pulled off its current call).</div>
              <div><label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Unit *</label>
                <select data-testid="dispatch-preempt-unit" value={pUnit} onChange={(e) => setPUnit(e.target.value)}
                  className="w-full bg-[#141518] border border-[#2C2D35] px-2 py-2 text-sm font-mono outline-none focus:border-[#F59E0B]">
                  <option value="">— select unit —</option>
                  {units.filter((u) => !(preemptInc.assigned_units || []).includes(u.unit_id)).map((u) => (
                    <option key={u.unit_id} value={u.unit_id}>{u.unit_id} · {u.status}{u.current_incident ? ` · on ${u.current_incident}` : ""}</option>
                  ))}
                </select></div>
              <div><label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Note (audited, optional)</label>
                <textarea data-testid="dispatch-preempt-note" value={pNote} onChange={(e) => setPNote(e.target.value)} rows={2}
                  className="w-full bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-sm font-mono outline-none focus:border-[#F59E0B]" /></div>
              <button data-testid="dispatch-preempt-submit" onClick={submitPreemptBoard} disabled={pBusy}
                className="w-full py-2 text-sm font-head bg-[#F59E0B] hover:bg-[#d97706] text-black font-bold transition-colors disabled:opacity-50">
                {pBusy ? "Assigning…" : "Confirm Assignment"}
              </button>
            </div>
          </div>
        </div>
      )}
      <ShiftBrief open={briefOpen} onClose={() => setBriefOpen(false)} />
      {/* LIVE TRAFFIC PEEK — Waze embed centered on the selected call */}
      {trafficInc && (
        <div data-testid="dispatch-traffic-modal" className="fixed inset-0 z-[999] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.75)" }} onClick={() => setTrafficInc(null)}>
          <div className="w-full max-w-4xl h-[75vh] bg-[#0A0A0A] border border-[#33CCFF] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-3 py-2 flex items-center gap-2 border-b border-[#2C2D35]">
              <Gauge size={15} className="text-[#33CCFF]" />
              <div className="min-w-0">
                <div className="font-head font-bold text-sm text-[#33CCFF]">LIVE TRAFFIC — {ref(trafficInc)} · {trafficInc.call_type_label}</div>
                <div className="font-mono text-[10px] text-[#9CA3AF] truncate">{trafficInc.location?.address} — jams · crashes · hazards (Waze community-reported)</div>
              </div>
              <button data-testid="dispatch-traffic-close" onClick={() => setTrafficInc(null)} className="ml-auto px-3 py-1.5 text-xs font-head font-bold text-white rounded bg-[#DC2626]">✕ CLOSE</button>
            </div>
            {trafficInc.location?.lat && trafficInc.location?.lng ? (
              <iframe title="Waze Live Traffic" className="flex-1 w-full" style={{ border: 0 }}
                src={`https://embed.waze.com/iframe?zoom=15&lat=${trafficInc.location.lat}&lon=${trafficInc.location.lng}&pin=1&ct=livemap`} allowFullScreen />
            ) : (
              <div className="flex-1 flex items-center justify-center font-mono text-xs text-[#9CA3AF] p-6 text-center">
                This call has no map coordinates yet (address not geocoded) — open the Waze traffic view from the dispatch map instead.
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
